/**
 * ActionRiskCategoriesView — Motor Neutro D2 · «Risco por Ação» (casa de config da A3).
 *
 * O Gestor/Admin vê as **17 ações canônicas** do motor agrupadas em 5 famílias, com o **piso de
 * identidade que esta configuração resolve** em cada uma, **de onde ele vem** (plataforma /
 * segmento / você) e o **dry-run** do que aconteceria ao mudar a categoria — antes de persistir.
 *
 * ⚠️ A copy diz «piso desta configuração», nunca «piso vigente» (Fable D2 I4): enquanto a casa
 * for dormente, o piso que VIGE é o da plataforma, e chamar o outro de vigente seria a própria
 * mentira de superfície que esta Frente existe para eliminar.
 *
 * ⚠️ PREMISSA STALE (auditoria de configuração §4.1, 11/Set/2026 + Ação 0, 14/Set, [3.853]):
 * este docblock afirmava «a casa é DORMENTE — `resolveConfiguredStructuralFloor` tem ZERO
 * call-sites no caminho quente». Media o símbolo ERRADO: o gate lê esta config desde
 * 27/Jul/2026 ([3.641]) via `applyAuthoredCategoryFloor` (que chama
 * `resolveConfiguredStructuralFloor`) em `evaluateIdentityGate`/`applyStepUp`, sob kill-switch
 * `LUME_IDENTITY_AUTHORED_RISK_FLOOR_ENABLED` (default ON). O banner «ainda não valem» abaixo
 * está, portanto, ERRADO — e continua de pé só até o MR de copy (spec da triagem, §5 linha 1):
 * a nova copy é decisão de produto, não deste docblock. Vigiado em
 * `apps/engine/test/fixtures/absence-claims.ts` (`projecao-de-risco-de-acao-nao-e-dormente`).
 *
 * ⚠️ O seletor de vertical é `PlaybookVertical` (eixo FUNCIONAL) — o mesmo que o gate resolve.
 * NÃO é o segmento do tenant (`TenantVertical`): os enums são disjuntos, e confundi-los foi o
 * defeito que a D2 consertou no Engine.
 *
 * Autoria gateada em `action-risk-category-author`; sem ela, leitura. Aprovação de rebaixamento
 * de leitura em `action-risk-category-platform-approve`. If-Match (ADR-071) em todas as
 * mutações; 409 é tratado de forma não-destrutiva.
 */

import { useMemo, useState } from 'react';

import { ApiError } from '../../lib/api-client';
import { useToastStore } from '../../stores/toast-store';

import {
  ACTION_FAMILIES,
  actionLabel,
  assuranceLabel,
  categoryLabel,
  sourceLabel,
  verticalLabel,
} from './action-risk-labels';
import {
  useApproveActionRiskCategory,
  useCreateActionRiskCategory,
  useDeleteActionRiskCategory,
  usePreviewActionRiskCategory,
  useUpdateActionRiskCategory,
} from './use-action-risk-categories';

import type { ResolvedActionView, ResolvedCatalogView, RiskCategory } from './types';

const CATEGORIES: readonly RiskCategory[] = [
  'none',
  'read-pii',
  'write-financial',
  'irreversible',
  'regulated-data',
];

export interface ActionRiskCategoriesViewProps {
  catalog: ResolvedCatalogView;
  /** Verticais FUNCIONAIS oferecidos no seletor (`null` = base agnóstica, sempre presente). */
  verticalOptions: readonly (string | null)[];
  vertical: string | null;
  onVerticalChange: (v: string | null) => void;
  canAuthor: boolean;
  canApprove: boolean;
}

function matchesQuery(row: ResolvedActionView, query: string): boolean {
  if (query.length === 0) return true;
  const q = query.toLowerCase();
  return actionLabel(row.action).toLowerCase().includes(q) || row.action.toLowerCase().includes(q);
}

export function ActionRiskCategoriesView({
  catalog,
  verticalOptions,
  vertical,
  onVerticalChange,
  canAuthor,
  canApprove,
}: ActionRiskCategoriesViewProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<string | null>(null);

  const byAction = useMemo(() => {
    const map = new Map<string, ResolvedActionView>();
    for (const a of catalog.actions) map.set(a.action, a);
    return map;
  }, [catalog.actions]);

  const authoredCount = catalog.actions.filter((a) => a.tenantOverride !== null).length;
  const pendingCount = catalog.actions.filter(
    (a) => a.tenantOverride?.approvalStatus === 'pending_platform_review',
  ).length;

  // Ações que o catálogo de famílias do FE ainda não mapeou — exibidas ao final para não
  // esconder dado real caso o motor ganhe uma ação nova (molde BondTypeConfigsView).
  const mapped = new Set(ACTION_FAMILIES.flatMap((f) => f.actions));
  const unmapped = catalog.actions.filter((a) => !mapped.has(a.action));

  const selectedRow = selected !== null ? byAction.get(selected) : undefined;

  return (
    <div className="action-risk-screen">
      <header className="arc-pagehead">
        <div>
          <div className="arc-pagehead__crumb">Configuração</div>
          <h1 className="arc-pagehead__title">Risco por Ação</h1>
          <p className="arc-pagehead__sub">
            {catalog.actions.length} ações do motor · <strong>{authoredCount}</strong> com regra sua
            {pendingCount > 0 ? (
              <>
                {' '}
                · <strong>{pendingCount}</strong> aguardando aprovação
              </>
            ) : null}
          </p>
        </div>
        <div className="arc-pagehead__spacer" />
        <label className="arc-vertical">
          <span className="arc-vertical__label">Vertical do playbook</span>
          <select
            className="arc-select"
            aria-label="Vertical do playbook para avaliar o piso"
            value={vertical ?? ''}
            onChange={(e) => onVerticalChange(e.target.value === '' ? null : e.target.value)}
          >
            {verticalOptions.map((v) => (
              <option key={v ?? '__base__'} value={v ?? ''}>
                {verticalLabel(v)}
              </option>
            ))}
          </select>
        </label>
        <input
          type="search"
          className="arc-search"
          placeholder="Buscar ação…"
          aria-label="Buscar ação"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </header>

      <div className="arc-page">
        {/* ★ O banner do estado dormente é PERMANENTE — não é aviso de canto. */}
        <div className="arc-banner arc-banner--warning" role="note">
          <span>
            <strong>Estas regras ainda não valem no atendimento.</strong> O motor já lê o piso de
            identidade a partir do padrão da plataforma; a leitura desta configuração pelo gate é
            uma etapa futura. O que você registra aqui fica auditado e pode ser simulado abaixo —
            mas <strong>não altera hoje</strong> o que a IA exige de um cliente.
          </span>
        </div>

        <div className="arc-banner arc-banner--info" role="note">
          <span>
            O piso é o <strong>mínimo de identificação</strong> que o motor exige antes de executar
            a ação. A plataforma define um piso que você pode <strong>endurecer</strong> — nunca
            afrouxar abaixo do mínimo estrutural. Rebaixar a categoria de uma <em>leitura</em> exige
            aprovação da plataforma.
          </span>
        </div>

        {!canAuthor && (
          <div className="arc-banner arc-banner--muted" role="note">
            <span>
              Você tem acesso de <strong>leitura</strong>. Registrar ou alterar regras de risco é
              restrito a perfis com autoria de configuração.
            </span>
          </div>
        )}

        <div className="arc-families">
          {ACTION_FAMILIES.map((family) => {
            const rows = family.actions
              .map((a) => byAction.get(a))
              .filter((r): r is ResolvedActionView => r !== undefined && matchesQuery(r, search));
            if (rows.length === 0) return null;
            return (
              <section
                key={family.id}
                className="arc-family"
                aria-label={`Família ${family.label}`}
              >
                <h2 className="arc-family__title">{family.label}</h2>
                {rows.map((row) => (
                  <ActionRow
                    key={row.action}
                    row={row}
                    isSelected={selected === row.action}
                    onSelect={() => setSelected(row.action)}
                  />
                ))}
              </section>
            );
          })}

          {unmapped.length > 0 && (
            <section className="arc-family" aria-label="Família Não classificadas">
              <h2 className="arc-family__title">Não classificadas</h2>
              {unmapped
                .filter((r) => matchesQuery(r, search))
                .map((row) => (
                  <ActionRow
                    key={row.action}
                    row={row}
                    isSelected={selected === row.action}
                    onSelect={() => setSelected(row.action)}
                  />
                ))}
            </section>
          )}
        </div>

        {selectedRow !== undefined && (
          <ActionDetailPanel
            row={selectedRow}
            vertical={vertical}
            canAuthor={canAuthor}
            canApprove={canApprove}
            onClose={() => setSelected(null)}
          />
        )}
      </div>
    </div>
  );
}

interface ActionRowProps {
  row: ResolvedActionView;
  isSelected: boolean;
  onSelect: () => void;
}

function ActionRow({ row, isSelected, onSelect }: ActionRowProps): JSX.Element {
  const authored = row.tenantOverride !== null;
  const pending = row.tenantOverride?.approvalStatus === 'pending_platform_review';
  const hardened = row.effective.structuralFloor !== row.platform.structuralFloor;

  return (
    <div className={`arc-row${isSelected ? ' arc-row--selected' : ''}`}>
      <div className="arc-row__main">
        {/* id canônico no title (diagnóstico) — não é copy do operador */}
        <span className="arc-row__name" title={row.action}>
          {actionLabel(row.action)}
        </span>
        <span className="arc-row__category">{categoryLabel(row.effective.category)}</span>
      </div>
      <span className={`arc-floor arc-floor--${row.effective.structuralFloor}`}>
        {assuranceLabel(row.effective.structuralFloor)}
      </span>
      <span className="arc-row__source">
        {authored ? (
          <span className="arc-tag arc-tag--authored" title="Regra registrada por este tenant">
            {hardened ? 'Você endureceu' : 'Regra sua'}
          </span>
        ) : (
          <span className="arc-muted">{sourceLabel(row.effective.source)}</span>
        )}
        {pending && (
          <span
            className="arc-tag arc-tag--pending"
            title="Rebaixa o piso de uma leitura — retido até aprovação da plataforma"
          >
            Aguardando aprovação
          </span>
        )}
      </span>
      <button type="button" className="arc-link" onClick={onSelect}>
        detalhes
      </button>
    </div>
  );
}

interface ActionDetailPanelProps {
  row: ResolvedActionView;
  vertical: string | null;
  canAuthor: boolean;
  canApprove: boolean;
  onClose: () => void;
}

function ActionDetailPanel({
  row,
  vertical,
  canAuthor,
  canApprove,
  onClose,
}: ActionDetailPanelProps): JSX.Element {
  const [candidate, setCandidate] = useState<RiskCategory>(row.effective.category);
  const preview = usePreviewActionRiskCategory();
  const create = useCreateActionRiskCategory();
  const update = useUpdateActionRiskCategory();
  const remove = useDeleteActionRiskCategory();
  const approve = useApproveActionRiskCategory();
  const toast = useToastStore((s) => s.push);

  const override = row.tenantOverride;
  const busy = create.isPending || update.isPending || remove.isPending || approve.isPending;

  const onError = (err: unknown): void => {
    if (err instanceof ApiError && err.status === 409) {
      toast({
        variant: 'info',
        title: 'A regra foi alterada por outra edição',
        description: 'Recarregamos a lista. Refaça a alteração.',
        durationMs: 5000,
      });
      return;
    }
    toast({ variant: 'error', title: 'Não foi possível aplicar a alteração.', durationMs: 6000 });
  };

  const handlePreview = (): void => {
    preview.mutate({
      action: row.action,
      ...(vertical !== null ? { vertical } : {}),
      category: candidate,
    });
  };

  const handleSave = (): void => {
    if (!canAuthor || busy) return;
    const done = (label: string) => (): void => {
      // Sem prometer efeito no atendimento: a casa é dormente (banner permanente diz o resto).
      toast({ variant: 'success', title: label, durationMs: 3000 });
    };
    if (override === null) {
      create.mutate(
        { action: row.action, category: candidate },
        { onSuccess: done('Regra registrada'), onError },
      );
    } else {
      update.mutate(
        { id: override.id, expectedVersion: override.version, body: { category: candidate } },
        { onSuccess: done('Regra atualizada'), onError },
      );
    }
  };

  return (
    <aside className="arc-panel" aria-label={`Detalhes de ${actionLabel(row.action)}`}>
      <div className="arc-panel__head">
        <h2 className="arc-panel__title" title={row.action}>
          {actionLabel(row.action)}
        </h2>
        <button type="button" className="arc-link" onClick={onClose}>
          Fechar
        </button>
      </div>

      {/*
        ★ Fable D2 I4 — a palavra tem de ser honesta SOZINHA. Este painel fica longe do banner
        de estado dormente (que sai do viewport ao rolar), então «vigente» aqui afirmaria uma
        vigência que o gate não honra: hoje ele impõe o piso da PLATAFORMA. Daí «Piso desta
        configuração» + a nota abaixo, que repete o essencial em uma linha.
        Nota de rito: comentário de revisão NUNCA vira copy do operador (a 1ª versão deste fix
        vazou o texto do achado para dentro do `<p>`).
      */}
      <p className="arc-panel__hint">
        Avaliado sob <strong>{verticalLabel(vertical)}</strong> — o gate ainda impõe o piso da
        plataforma; o piso desta configuração vale quando o motor passar a lê-la.
      </p>

      <dl className="arc-facets">
        <div className="arc-facets__row">
          <dt>Piso desta configuração</dt>
          <dd>
            <strong>{assuranceLabel(row.effective.structuralFloor)}</strong>
          </dd>
        </div>
        <div className="arc-facets__row">
          <dt>Piso da plataforma</dt>
          <dd>{assuranceLabel(row.platform.structuralFloor)}</dd>
        </div>
        <div className="arc-facets__row">
          <dt>Categoria</dt>
          <dd title={row.effective.category}>{categoryLabel(row.effective.category)}</dd>
        </div>
        <div className="arc-facets__row">
          <dt>Origem da categoria</dt>
          <dd>{sourceLabel(row.effective.source)}</dd>
        </div>
        <div className="arc-facets__row">
          <dt>Mínimo pela consequência</dt>
          <dd title="Imposto pelo motor — o que a ação FAZ. Config só soma por cima.">
            {assuranceLabel(row.effective.consequenceFloor)}
          </dd>
        </div>
        <div className="arc-facets__row">
          <dt>Mínimo pela categoria</dt>
          <dd>{assuranceLabel(row.effective.categoryFloor)}</dd>
        </div>
      </dl>

      <div className="arc-authoring">
        <label className="arc-field">
          <span className="arc-field__label">Categoria de risco</span>
          <select
            className="arc-select"
            aria-label="Categoria de risco a simular"
            value={candidate}
            disabled={!canAuthor}
            onChange={(e) => {
              setCandidate(e.target.value as RiskCategory);
              // Fable D2 M4: sem isto a simulação ANTERIOR fica ao lado da seleção nova,
              // afirmando um piso que já não corresponde à categoria escolhida.
              preview.reset();
            }}
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {categoryLabel(c)}
              </option>
            ))}
          </select>
        </label>

        <div className="arc-actions">
          <button
            type="button"
            className="arc-btn arc-btn--ghost"
            onClick={handlePreview}
            disabled={preview.isPending}
          >
            Simular
          </button>
          <button
            type="button"
            className="arc-btn"
            onClick={handleSave}
            disabled={!canAuthor || busy}
          >
            {override === null ? 'Registrar regra' : 'Atualizar regra'}
          </button>
        </div>
      </div>

      {preview.data !== undefined && (
        <div className="arc-preview" role="status">
          <h3 className="arc-preview__title">Simulação (não salva)</h3>
          <p>
            Piso resultante: <strong>{assuranceLabel(preview.data.preview.structuralFloor)}</strong>{' '}
            <span className="arc-muted">
              (consequência {assuranceLabel(preview.data.preview.consequenceFloor)} · categoria{' '}
              {assuranceLabel(preview.data.preview.categoryFloor)})
            </span>
          </p>
          <p className={preview.data.verdict.requiresPlatformApproval ? 'arc-verdict--hold' : ''}>
            {preview.data.verdict.requiresPlatformApproval
              ? 'Rebaixa o piso de uma leitura — ficará retido até aprovação da plataforma.'
              : 'Não rebaixa o piso da plataforma.'}
          </p>
        </div>
      )}

      {override !== null && (
        <div className="arc-override">
          <h3 className="arc-preview__title">Sua regra</h3>
          <p>
            {categoryLabel(override.category)} ·{' '}
            {override.approvalStatus === 'pending_platform_review' ? (
              <span className="arc-tag arc-tag--pending">Aguardando aprovação</span>
            ) : (
              <span className="arc-tag arc-tag--authored">Ativa</span>
            )}
          </p>
          <div className="arc-actions">
            {override.approvalStatus === 'pending_platform_review' && canApprove && (
              <button
                type="button"
                className="arc-btn arc-btn--ghost"
                disabled={busy}
                onClick={() =>
                  approve.mutate(
                    { id: override.id, expectedVersion: override.version },
                    {
                      onSuccess: () =>
                        toast({
                          variant: 'success',
                          title: 'Regra aprovada — passa a valer quando o gate ler esta config',
                          durationMs: 4000,
                        }),
                      onError,
                    },
                  )
                }
              >
                Aprovar
              </button>
            )}
            {canAuthor && (
              <button
                type="button"
                className="arc-btn arc-btn--danger"
                disabled={busy}
                onClick={() =>
                  remove.mutate(
                    { id: override.id, expectedVersion: override.version },
                    {
                      onSuccess: () =>
                        toast({
                          variant: 'success',
                          title: 'Regra removida do registro',
                          durationMs: 3000,
                        }),
                      onError,
                    },
                  )
                }
              >
                Remover
              </button>
            )}
          </div>
        </div>
      )}
    </aside>
  );
}
